import { Status } from 'hooks/requests/fetchUtils';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import useFetchApi from 'hooks/useFetchApi';
import useAnalytics, { Event } from 'hooks/useAnalytics';
import useIovation from 'hooks/useIovation';
import useQueryParameters from 'hooks/useQueryParameters';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { mergeIssuance } from 'state/unifiedIssuance';

const PATH = '/api/financing_virtual_card_issuance';

export interface UnifiedVirtualCardIssuanceRequest {
  applicationId: string | null;
}

type UnifiedVirtualCardIssuanceHook = () => [
  (
    params: UnifiedVirtualCardIssuanceRequest,
  ) => Promise<UnifiedVirtualCardIssuanceResponse>,
  Status,
  boolean,
];

export interface UnifiedVirtualCardIssuanceResponse {
  issuanceId: string;
  issuanceStatus: string;
  cardStatus?: string;
  cardCapacityValue?: number;
  accountNumber?: string;
  hasWallets: boolean;
}

export class UnifiedVirtualCardIssuanceError extends Error {}

const useUnifiedVirtualCardIssuance: UnifiedVirtualCardIssuanceHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const dispatch = useDispatch();
  const iovationBlackbox = useIovation();
  const { sendEvents } = useAnalytics();
  const { setAnalyticsVariables } = useAnalyticsVariables();

  const { mockVCI } = useQueryParameters();

  const unifiedVirtualCardIssuanceRequest = useCallback(
    async (
      params: UnifiedVirtualCardIssuanceRequest,
    ): Promise<UnifiedVirtualCardIssuanceResponse> => {
      setError(false);

      const blackbox = await iovationBlackbox;

      const responsePromise = fetch(PATH, {
        method: 'POST',
        body: {
          ...params,
          mockResponse: mockVCI,
          iovationBlackbox: blackbox,
        },
      });

      sendEvents([Event.IssuanceCallMade]);
      const response = await responsePromise;

      if (response.ok) {
        const result: UnifiedVirtualCardIssuanceResponse = await response.json();
        dispatch(
          mergeIssuance({
            issuanceId: result.issuanceId,
            hasWallets: result.hasWallets,
          }),
        );
        setAnalyticsVariables({
          virtualCardIssuanceStatus: result.issuanceStatus,
          virtualCardStatus: result.cardStatus,
        });
        return Promise.resolve(result);
      } else {
        setError(true);
        return Promise.reject(new UnifiedVirtualCardIssuanceError());
      }
    },
    [
      iovationBlackbox,
      fetch,
      mockVCI,
      dispatch,
      sendEvents,
      setAnalyticsVariables,
    ],
  );

  return [unifiedVirtualCardIssuanceRequest, status, error];
};

export default useUnifiedVirtualCardIssuance;
